// ═══════════════════════════════════════════════════════════════════
//  HeatmapLayer — block-density / connector-flow heatmap on the Minimap.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase F of MINIMAP_ATLAS_PLAN. Activates the dormant
//  src/lib/minimapHeatmap.ts helper. Renders a 10×10 grid of colored
//  rectangles overlaid on the canvas; empty cells fully transparent.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import type { WorkflowBlock, Connector } from '@/types/workflow';
import { buildMinimapHeatmap, type HeatmapMode } from '@/lib/minimapHeatmap';
import type { MinimapMode } from '@/types/minimap';

export interface HeatmapLayerProps {
  mode: MinimapMode;
  blocks: WorkflowBlock[];
  connectors: Connector[];
  /** Logical canvas bounds passed from the Minimap math. */
  canvasWidth: number;
  canvasHeight: number;
  /** Pixel dimensions of the minimap container. */
  width: number;
  height: number;
  toScreenX: (x: number) => number;
  toScreenY: (y: number) => number;
  scale: number;
}

const ROWS = 10;
const COLS = 10;

export function HeatmapLayer({
  mode,
  blocks,
  connectors,
  canvasWidth,
  canvasHeight,
  width,
  height,
  toScreenX,
  toScreenY,
  scale,
}: HeatmapLayerProps) {
  const heatmap = useMemo(() => {
    if (mode === 'normal') return null;
    const heatmapMode: HeatmapMode = mode === 'heatmap-density' ? 'block-density' : 'connector-flow';
    return buildMinimapHeatmap({
      blocks: blocks.map((b) => ({ id: b.id, x: b.x, y: b.y })),
      connectors: connectors.map((c) => ({ id: c.id, fromId: c.fromId, toId: c.toId })),
      mode: heatmapMode,
      rows: ROWS,
      cols: COLS,
      bounds: {
        minX: -canvasWidth / 2,
        minY: -canvasHeight / 2,
        maxX: canvasWidth / 2,
        maxY: canvasHeight / 2,
      },
    });
  }, [mode, blocks, connectors, canvasWidth, canvasHeight]);

  if (!heatmap) return null;

  const cellWidth = canvasWidth / COLS;
  const cellHeight = canvasHeight / ROWS;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
      data-fw-minimap-heatmap={mode}
    >
      {heatmap.colors.map((row, ri) =>
        row.map((cell, ci) => {
          if (cell.alpha === 0) return null;
          const logicalX = -canvasWidth / 2 + ci * cellWidth;
          const logicalY = -canvasHeight / 2 + ri * cellHeight;
          return (
            <rect
              key={`${ri}-${ci}`}
              x={toScreenX(logicalX) * scale}
              y={toScreenY(logicalY) * scale}
              width={cellWidth * scale}
              height={cellHeight * scale}
              fill={`rgba(${cell.r}, ${cell.g}, ${cell.b}, ${cell.alpha})`}
            />
          );
        }),
      )}
    </svg>
  );
}
